import { useEffect } from 'react';
import { Link } from 'react-router-dom';

/* ── tiny icons (inline SVGs to avoid deps) ──────────────────────────── */

function IconCode() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25M6.75 6.75L1.5 12l5.25 5.25M14.25 3.75l-4.5 16.5" />
    </svg>
  );
}

function IconChat() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9M7.5 12h4.5m-10.5 7.5L4.5 15H3a2.25 2.25 0 01-2.25-2.25v-9A2.25 2.25 0 013 1.5h18a2.25 2.25 0 012.25 2.25v9A2.25 2.25 0 0121 15h-5.25l-5.25 4.5z" />
    </svg>
  );
}

function IconPen() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
    </svg>
  );
}

function IconArrowRight() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
  );
}

function IconBook() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  );
}

function IconLightbulb() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
    </svg>
  );
}

/* ── reusable pieces ─────────────────────────────────────────────────── */

function Card({ children, className = '' }) {
  return (
    <div className={`rounded-xl border p-5 border-gray-200 bg-gray-50/50 dark:border-brand-50/10 dark:bg-brand-800/40 ${className}`}>
      {children}
    </div>
  );
}

function ToolCard({ to, icon, name, description }) {
  return (
    <Link
      to={to}
      className="group flex flex-col gap-3 rounded-xl border p-5 transition-all
        border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm
        dark:border-brand-50/10 dark:bg-brand-800/40 dark:hover:border-brand-50/20 dark:hover:bg-brand-800/60"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5 text-gray-700 dark:text-brand-200">
          {icon}
          <span className="font-semibold text-[15px]">{name}</span>
        </div>
        <span className="text-gray-300 group-hover:text-gray-500 dark:text-brand-700 dark:group-hover:text-brand-400 transition-colors">
          <IconArrowRight />
        </span>
      </div>
      <p className="text-[14px] leading-relaxed text-gray-500 dark:text-brand-500">{description}</p>
    </Link>
  );
}

function CapabilityCard({ icon, title, description, items }) {
  return (
    <Card>
      <div className="flex items-center gap-2.5 mb-3 text-gray-700 dark:text-brand-200">
        {icon}
        <h3 className="font-semibold text-[15px]">{title}</h3>
      </div>
      <p className="text-[14px] leading-relaxed text-gray-500 dark:text-brand-500 mb-3">{description}</p>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="text-[13px] text-gray-500 dark:text-brand-500 flex items-start gap-2">
            <span className="text-gray-300 dark:text-brand-600 mt-0.5 shrink-0">-</span>
            {item}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/* ── main component ──────────────────────────────────────────────────── */

export default function IntegrationsOverview({ onHeadingsExtracted }) {
  useEffect(() => {
    if (onHeadingsExtracted) {
      onHeadingsExtracted([
        { text: 'What changes', level: 2, id: 'what-changes' },
        { text: 'Connect your tools', level: 2, id: 'connect-your-tools' },
        { text: 'What your tools can do', level: 2, id: 'what-your-tools-can-do' },
        { text: 'Try it', level: 2, id: 'try-it' },
      ]);
    }
  }, [onHeadingsExtracted]);

  return (
    <article className="min-w-0 max-w-[680px]">
      {/* Hero */}
      <h1 className="text-3xl font-bold tracking-tighter mt-0 mb-3 leading-tight text-gray-900 dark:text-brand-50">
        Connect an AI client
      </h1>
      <p className="text-[17px] leading-relaxed mb-8 text-gray-500 dark:text-brand-400">
        Connect Cursor, Claude, Figma, or any MCP-compatible tool to your live strategy. Your tools read it directly, ask strategic questions, and report what they learn - no copy-paste, no stale context.
      </p>

      {/* Before / After */}
      <p className="mb-8 text-[15px] leading-relaxed text-gray-600 dark:text-brand-200">Already building? <Link to="/docs/install-pabs" className="font-semibold underline underline-offset-4">Bring Pabs into your tool</Link>. You don't need a plan first: your tool sets up your Brief and strategy from the project. The instructions below connect a plan you already have.</p>
      <h2 id="what-changes" className="text-xl font-semibold tracking-tight mt-10 mb-5 leading-snug scroll-mt-20 text-gray-900 dark:text-brand-50">
        What changes
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
        <Card>
          <p className="text-[11px] font-bold uppercase tracking-widest mb-2.5 text-gray-400 dark:text-brand-600">Without integrations</p>
          <ul className="space-y-2 text-[14px] text-gray-500 dark:text-brand-500">
            <li>Copy strategy into prompts manually</li>
            <li>Context goes stale immediately</li>
            <li>Insights stay trapped in your editor</li>
            <li>Strategy and build drift apart</li>
          </ul>
        </Card>
        <Card className="border-gray-300 dark:border-brand-50/20">
          <p className="text-[11px] font-bold uppercase tracking-widest mb-2.5 text-gray-700 dark:text-brand-200">With integrations</p>
          <ul className="space-y-2 text-[14px] text-gray-600 dark:text-brand-400">
            <li>Tools read the live strategy directly</li>
            <li>Every read is the current state</li>
            <li>Findings flow back to PABS automatically</li>
            <li>Strategy and build stay in sync</li>
          </ul>
        </Card>
      </div>

      {/* How it works - one line */}
      <Card className="mb-10">
        <p className="text-[14px] leading-relaxed text-gray-600 dark:text-brand-400">
          PABS uses <strong className="font-semibold text-gray-800 dark:text-brand-200">MCP</strong> (Model Context Protocol) - an open standard that lets AI tools connect to external services. You don't need to understand the protocol. Just follow the setup for your tool.
        </p>
      </Card>

      {/* Tool cards */}
      <h2 id="connect-your-tools" className="text-xl font-semibold tracking-tight mt-10 mb-5 leading-snug scroll-mt-20 text-gray-900 dark:text-brand-50">
        Connect your tools
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
        <ToolCard
          to="/docs/integrate-cursor"
          icon={<IconCode />}
          name="Cursor"
          description="AI code editor. Read your strategy while you build."
        />
        <ToolCard
          to="/docs/integrate-claude"
          icon={<IconChat />}
          name="Claude Code & Desktop"
          description="Strategy-aware conversations and coding."
        />
        <ToolCard
          to="/docs/integrate-figma"
          icon={<IconPen />}
          name="Figma"
          description="Review designs against your product strategy."
        />
        <ToolCard
          to="/docs/integrate-other"
          icon={<IconSearch />}
          name="Other tools"
          description="Windsurf, Replit, GitHub Copilot, and any MCP client."
        />
      </div>

      <p className="text-[14px] text-gray-500 dark:text-brand-500 mb-10">
        All integrations start the same way:{' '}
        <Link to="/docs/integrations-setup" className="underline underline-offset-2 text-gray-700 dark:text-brand-300 hover:text-gray-900 dark:hover:text-brand-50 transition-colors">
          get your connection URL
        </Link>{' '}
        from your plan, then paste it into your tool.
      </p>

      {/* Capabilities */}
      <h2 id="what-your-tools-can-do" className="text-xl font-semibold tracking-tight mt-10 mb-5 leading-snug scroll-mt-20 text-gray-900 dark:text-brand-50">
        What your tools can do
      </h2>

      <div className="grid grid-cols-1 gap-4 mb-10">
        <CapabilityCard
          icon={<IconBook />}
          title="Read your strategy"
          description="Your tools pull any part of your plan - live, always current."
          items={[
            'Product brief, strategy, goals, and metrics',
            'Competitive positioning and differentiation',
            'Action plan with timelines and deliverables',
            'Open decisions, hypotheses, and assumptions',
            'Generated documents - PRD, SOW, and more',
          ]}
        />
        <CapabilityCard
          icon={<IconChat />}
          title="Ask strategic questions"
          description="Your tool asks whether what you're building aligns with strategy. PABS reasons against the live plan and returns an assessment."
          items={[
            'Alignment score (0–100) with explanation',
            'Which hypotheses and assumptions apply',
            'Recommended next action',
            'Observations auto-captured as learnings',
          ]}
        />
        <CapabilityCard
          icon={<IconLightbulb />}
          title="Report learnings"
          description="When your tool discovers something unexpected, it sends the finding to PABS for the strategist to review."
          items={[
            'User behaviours and market signals',
            'Technical constraints and surprises',
            'Evidence from testing or research',
            'Source tracked (which tool reported it)',
          ]}
        />
      </div>

      {/* Try it */}
      <h2 id="try-it" className="text-xl font-semibold tracking-tight mt-10 mb-5 leading-snug scroll-mt-20 text-gray-900 dark:text-brand-50">
        Try it
      </h2>

      <p className="text-[15px] leading-relaxed mb-4 text-gray-600 dark:text-brand-400">
        Once connected, try these prompts in your tool:
      </p>

      <div className="space-y-3 mb-10">
        {[
          'What is my product strategy?',
          'Does this feature align with my hypotheses?',
          'What are my open decisions?',
          'I found that users are ignoring the onboarding - report this as a learning',
          'Summarise my competitor analysis',
        ].map((prompt, i) => (
          <div
            key={i}
            className="rounded-lg border px-4 py-3 text-[14px] italic
              border-gray-200 bg-gray-50 text-gray-600
              dark:border-brand-50/10 dark:bg-brand-800/40 dark:text-brand-400"
          >
            "{prompt}"
          </div>
        ))}
      </div>

      {/* Footer links */}
      <div className="flex flex-wrap gap-3 text-[13px]">
        <Link
          to="/docs/integrations-setup"
          className="inline-flex items-center gap-1.5 rounded-lg border px-4 py-2 font-medium transition-colors
            border-gray-200 text-gray-700 hover:bg-gray-50
            dark:border-brand-50/10 dark:text-brand-300 dark:hover:bg-brand-800/60"
        >
          Get started <IconArrowRight />
        </Link>
        <Link
          to="/docs/ask-pabs-agent"
          className="inline-flex items-center gap-1.5 rounded-lg border px-4 py-2 font-medium transition-colors
            border-gray-200 text-gray-700 hover:bg-gray-50
            dark:border-brand-50/10 dark:text-brand-300 dark:hover:bg-brand-800/60"
        >
          ask_pabs_agent reference <IconArrowRight />
        </Link>
      </div>
    </article>
  );
}
